import {
  createContext,
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useState,
} from 'react';

import { type BaseRoute, type Path, type RouteName } from '../';

export type SubMenuDisplay = {
  parentRoutePath: Path;
  parentRouteName: RouteName;
  subroutes: BaseRoute[];
};

export type SidebarContextType = {
  shouldShowSubMenu: boolean;
  subMenu: SubMenuDisplay | null;
  showSubMenu: (params: SubMenuDisplay) => void;
  updateSubMenu: (params: SubMenuDisplay) => void;
  hideSubMenu: () => void;
  resetSubMenu: () => void;
};

export const SidebarContext = createContext<SidebarContextType | null>(null);

type SidebarContextProviderProps = PropsWithChildren;

export const SidebarContextProvider = ({
  children,
}: SidebarContextProviderProps): ReactElement => {
  const [shouldShowSubMenu, setShouldShowSubMenu] =
    useState<SidebarContextType['shouldShowSubMenu']>(false);

  const [subMenu, setSubMenu] = useState<SidebarContextType['subMenu']>(null);

  const showSubMenu: SidebarContextType['showSubMenu'] = useCallback(
    (params) => {
      setShouldShowSubMenu((prev) => !prev);
      setSubMenu({ ...params });
    },
    [],
  );

  const hideSubMenu: SidebarContextType['hideSubMenu'] =
    useCallback((): void => {
      shouldShowSubMenu && setShouldShowSubMenu(false);
    }, [shouldShowSubMenu]);

  const updateSubMenu: SidebarContextType['updateSubMenu'] = useCallback(
    (params) => {
      setSubMenu({ ...params });
    },
    [],
  );

  const resetSubMenu: SidebarContextType['resetSubMenu'] = useCallback(() => {
    setSubMenu(null);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        shouldShowSubMenu,
        subMenu,
        showSubMenu,
        updateSubMenu,
        hideSubMenu,
        resetSubMenu,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
